import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../core/branding/BrandingContext';
import { DashboardConfig, DashboardOption } from '../core/types/tenant.types';
import { getApiEndpoint } from '../core/config/apiConfig';
import { useAuth } from '../components/auth/SimpleAuthProvider';

export const CombinedDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { branding, projectName } = useBranding();
  const { user, logout } = useAuth();
  const [dashboardConfig, setDashboardConfig] = useState<DashboardConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDashboardConfig = async () => {
      try {
        const response = await fetch(
          getApiEndpoint(`config/${projectName}/dashboard-options`)
        );
        const config = await response.json();
        setDashboardConfig(config);
      } catch (error) {
        console.error('Failed to load dashboard config:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardConfig();
  }, [projectName]);

// frontend/src/pages/CombinedDashboardPage.tsx
// frontend/src/pages/CombinedDashboardPage.tsx
const handleOptionClick = (option: DashboardOption) => {
  if (!user) return;
  
  // Store which catalog to load
  sessionStorage.setItem('selected_catalog', option.catalogFile || 'catalog_all.json');
  sessionStorage.setItem('selected_option_title', option.title);
  
  // Navigate to map page (SAME APP, NO SUBDOMAIN CHANGE!)
  navigate('/map');
};

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (isLoading || !branding) {
    return <div>Loading...</div>;
  }

  return (
    <div 
      className="min-h-screen" 
      style={{ backgroundColor: branding.backgroundColor }}
    >
      {/* Header */}
      <header
        className="shadow-md py-6 px-12 flex justify-between items-center"
        style={{ backgroundColor: branding.primaryColor }}
      >
        <div className="flex items-center space-x-6">
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={`${projectName} logo`}
              className="2xl:h-32"
              style={{ height: 80, width: 'auto' }}
            />
          )}
          <h1 className="text-3xl 2xl:text-5xl font-bold text-white">
            {dashboardConfig?.displayName || projectName}
          </h1>
        </div>

        <div className="flex items-center space-x-6">
          <span className="text-white text-base 2xl:text-xl font-medium">
            {user?.firstName ? `${user.firstName} ${user.lastName}`.trim() : user?.email}
          </span>
          <button
            onClick={handleLogout}
            className="px-6 py-2 2xl:px-8 2xl:py-4 text-base 2xl:text-xl bg-white rounded-lg hover:bg-gray-100 font-semibold"
            style={{ color: branding.primaryColor }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-12 py-16 2xl:py-24">
        <div className="mb-12 2xl:mb-20 text-center">
          <h2
            className="text-4xl 2xl:text-6xl font-bold mb-4 2xl:mb-6"
            style={{ color: branding.primaryColor }}
          >
            Welcome, {user?.firstName || user?.email}! 👋
          </h2>
          <p className="text-gray-600 text-lg 2xl:text-2xl max-w-3xl 2xl:max-w-5xl mx-auto">
            {dashboardConfig?.description}
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 2xl:gap-12 max-w-6xl 2xl:max-w-[1400px] mx-auto">
          {dashboardConfig?.options?.map((option) => (
            <div
              key={option.id}
              onClick={() => handleOptionClick(option)}
              className="bg-white rounded-xl shadow-lg p-8 2xl:p-14 cursor-pointer hover:shadow-2xl transition-shadow"
            >
              <div className="text-6xl 2xl:text-8xl mb-6 2xl:mb-8 text-center">{option.icon}</div>
              <h3
                className="text-2xl 2xl:text-4xl font-bold mb-4 2xl:mb-6 text-center"
                style={{ color: branding.primaryColor }}
              >
                {option.title}
              </h3>
              <p className="text-gray-600 text-base 2xl:text-xl text-center leading-relaxed">
                {option.description}
              </p>
              <div className="mt-6 2xl:mt-10 text-center">
                <button
                  className="px-8 py-3 2xl:px-12 2xl:py-5 text-base 2xl:text-xl rounded-lg text-white font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  Launch {option.title} →
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};